import React, { ChangeEvent, FC, useState } from 'react';
import { TextField, TextFieldCustomProps } from '../TextField';
import { IconButton, IconButtonVariants } from '@/components/atoms/IconButton';
import { Icon } from '@/components/atoms/Icon';
import { ValidationItem } from '@/components/atoms/ValidationItem';
import styles from './PasswordField.module.scss';
import { AnimateHeight } from '@/components/helpers/AnimateHeight';

export type RequirementItem = {
  label: string;
  valid: boolean;
};

export type PasswordFieldProps = Omit<TextFieldCustomProps, 'type' | 'endAdornment'> & {
  requirements?: RequirementItem[];
};

const PasswordField: FC<PasswordFieldProps> = ({ requirements, ...props }) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isFocusedOnce, setIsFocusedOnce] = useState(false);

  const toggleVisibility = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setIsPasswordVisible((prev) => !prev);
  };

  const handleFocus = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (!isFocusedOnce) {
      setIsFocusedOnce(true);
    }
    props.onFocus?.(e);
  };

  const shouldShowRequirements = !!requirements?.length && isFocusedOnce;

  return (
    <div className={styles.field}>
      <TextField
        {...props}
        onFocus={handleFocus}
        type={isPasswordVisible ? 'text' : 'password'}
        endAdornment={
          <IconButton
            size="big"
            variant={IconButtonVariants.SIMPLE}
            type="button"
            onClick={toggleVisibility}
            onMouseDown={(e) => e.preventDefault()}
            tabIndex={-1}
            disabled={!props.value}
            Icon={() => (
              <Icon
                name={isPasswordVisible ? 'general/view-off/24' : 'general/view/24'}
                size={24}
                color={props.value ? 'var(--icon-secondary)' : 'var(--icon-disabled)'}
              />
            )}
          />
        }
      />

      <AnimateHeight in={shouldShowRequirements} className={styles.requirements}>
        {requirements?.map((req, idx) => (
          <ValidationItem key={idx} text={req.label} type={req.valid ? 'success' : 'default'} />
        ))}
      </AnimateHeight>
    </div>
  );
};

export { PasswordField };
